import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { LIST_MY_APPOINTMENTS } from '../../graphql/appointments';

interface Appointment {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
}

interface ListMyAppointmentsData {
  listMyAppointments: Appointment[];
}

export function UpcomingAppointmentsWidget() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const { data, loading } = useQuery<ListMyAppointmentsData>(LIST_MY_APPOINTMENTS, {
    variables: {
      startDate: today,
      endDate: tomorrow,
    },
    fetchPolicy: 'network-only',
  });

  const appointments = data?.listMyAppointments || [];
  
  // Filter to only show today's appointments
  const todayAppointments = appointments.filter((apt: any) => {
    const aptDate = new Date(apt.startTime).toISOString().split('T')[0];
    return aptDate === today;
  });

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const isUpcoming = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
    return diffHours < 2 && diffHours > 0;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Today's Appointments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="inquiry-card flex flex-col h-[500px]">
      <CardHeader className="flex-shrink-0">
        <CardTitle>Today's Appointments</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden flex flex-col">
        {todayAppointments.length === 0 ? (
          <div className="flex flex-col h-full">
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No appointments today</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 flex-shrink-0">
              <Button 
                variant="outline" 
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => navigate('/calendar')}
              >
                View Full Calendar
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
              <div className="overflow-hidden border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Appointment
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {todayAppointments.map((appointment: any) => (
                      <tr
                        key={appointment.id}
                        onClick={() => navigate('/calendar')}
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                          isUpcoming(appointment.startTime) ? 'bg-red-50' : ''
                        }`}
                      >
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-xs text-aalaw-red font-medium">
                            {formatTime(appointment.startTime)}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {appointment.title}
                          </div>
                          {appointment.description && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              {appointment.description}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {appointment.location ? (
                            <div className="flex items-center gap-1">
                              <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="text-xs text-gray-600 truncate">
                                {appointment.location}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No location</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 flex-shrink-0">
              <Button 
                variant="outline" 
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => navigate('/calendar')}
              >
                View Full Calendar
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
